<script setup lang="ts" generic="T extends string">
// A row of filter chips with counts (Sierra's filter bars). Single selection by
// default (`modelValue` is a value); `multiple` makes `modelValue` an array and each
// chip a toggle. The arrow keys move between chips; Space or Enter presses one.
// `label` names the group.
import { ref } from 'vue';
import Chip from './Chip.vue';

const props = withDefaults(defineProps<{ modelValue: T | readonly T[]; options: readonly { value: T; label: string; count?: number }[]; multiple?: boolean; label: string }>(), {
  multiple: false,
});
const emit = defineEmits<{ 'update:modelValue': [value: T | T[]] }>();
const group = ref<HTMLElement | null>(null);

const selected = (value: T): boolean => (Array.isArray(props.modelValue) ? props.modelValue.includes(value) : props.modelValue === value);

function toggle(value: T): void {
  if (!props.multiple) {
    emit('update:modelValue', value);
    return;
  }
  const current = Array.isArray(props.modelValue) ? (props.modelValue as readonly T[]) : [];
  emit('update:modelValue', current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]);
}

function onKey(event: KeyboardEvent): void {
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
  if (step === undefined) return;
  const chips = [...(group.value?.querySelectorAll<HTMLElement>('button') ?? [])];
  const index = chips.indexOf(document.activeElement as HTMLElement);
  if (index === -1) return;
  event.preventDefault();
  chips[(index + step + chips.length) % chips.length]?.focus();
}
</script>

<template>
  <div ref="group" class="itc-chip-group" role="group" :aria-label="label" @keydown="onKey">
    <Chip v-for="option in options" :key="option.value" :label="option.label" :count="option.count" :pressed="selected(option.value)" @toggle="toggle(option.value)" />
  </div>
</template>

<style scoped>
.itc-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
